import React from 'react';
import { useQuery } from '@apollo/client/react';
import DetailsCardDetailList, {
    formatDate,
    formatStringOrNumber,
    SubjectProfileDetailsCardItem,
} from './details-card-detail-list';
import styles from './index.module.scss';
import SubjectProfileDetailsMiniWidgets from './mini-widgets';
import Badge from '@components-clean/core/Badge';
import Card from '@components-clean/core/card';
import { InfoField } from '@components-clean/core/info-field';
import Loader from '@components-clean/core/loader';
import { useTranslation } from '@hooks/index';
import { GET_STUDY_CONFIG } from '@queries/studies';
import { GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION } from '@queries/subject-monitoring';
import { SubjectMonitoringAPI } from '@apis/bff-api/subject-monitoring/subject-profile';

type Props = {
    studyRef: string;
    datasetId: number;
    datasetCreatedDate: Date;
    subjectId: number;
};

const SubjectProfileDetailsSubjectStatusCard = ({ studyRef, datasetId, datasetCreatedDate, subjectId }: Props) => {
    const { t } = useTranslation();

    const { t: tSubjectProfile } = useTranslation('sm', {
        keyPrefix: 'subjectProfile.subjectStatus',
    });

    const getStudyQuery = useQuery(GET_STUDY_CONFIG, {
        variables: {
            id: studyRef,
            timestamp: datasetCreatedDate,
        },
        fetchPolicy: 'no-cache',
    });
    const getSubjectDetailsQuery = SubjectMonitoringAPI.useGetSubjectDetails(studyRef, datasetId, subjectId);
    const getSubjectProfileDetailsConfigQuery = useQuery(GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION, {
        variables: { studyId: studyRef },
    });

    const study = getStudyQuery.data?.study;
    const subjectDetails = getSubjectDetailsQuery.data;
    const sectionConfig =
        getSubjectProfileDetailsConfigQuery.data?.subjectProfileDetailsConfiguration
            ?.subjectProfileDetailsConfigurationItem?.subjectStatus;

    const isLoading =
        getStudyQuery.loading || getSubjectDetailsQuery.isLoading || getSubjectProfileDetailsConfigQuery.loading;

    const isError =
        !!getStudyQuery.error || !!getSubjectDetailsQuery.error || !!getSubjectProfileDetailsConfigQuery.error;

    const items: SubjectProfileDetailsCardItem[] = [];

    if (sectionConfig?.screening) {
        items.push({
            key: 'screeningDate',
            label: tSubjectProfile('screeningDate'),
            value: formatDate(subjectDetails?.screeningDate),
        });
    }
    if (sectionConfig?.screenFail) {
        items.push({
            key: 'screenFail',
            label: tSubjectProfile('screenFailDate'),
            value: formatDate(subjectDetails?.screeningFailDate),
        });
    }
    if (sectionConfig?.screenFailReason) {
        items.push({
            key: 'screenFailReason',
            label: tSubjectProfile('screenFailReason'),
            value: formatStringOrNumber(subjectDetails?.screeningFailReason),
        });
    }
    if (sectionConfig?.enrolled) {
        items.push({
            key: 'enrolled',
            label: tSubjectProfile('enrolled'),
            value: formatDate(subjectDetails?.enrolledDate),
        });
    }
    if (sectionConfig?.randomized) {
        items.push({
            key: 'randomized',
            label: tSubjectProfile('randomized'),
            value: formatDate(subjectDetails?.randomizedDate),
        });
    }
    if (sectionConfig?.fUStart) {
        items.push({
            key: 'fUStart',
            label: tSubjectProfile('fUStartDate'),
            value: formatDate(subjectDetails?.fuStartDate),
        });
    }
    if (sectionConfig?.fUCompleted) {
        items.push({
            key: 'fUCompleted',
            label: tSubjectProfile('fUCompletedDate'),
            value: formatDate(subjectDetails?.fuCompletedDate),
        });
    }
    if (sectionConfig?.studyDisc) {
        items.push({
            key: 'studyDisc',
            label: tSubjectProfile('studyDisc'),
            value: formatDate(subjectDetails?.studyDiscontinuedDate),
        });
    }
    if (sectionConfig?.studyDiscReason) {
        items.push({
            key: 'studyDiscReason',
            label: tSubjectProfile('studyDiscReason'),
            value: formatStringOrNumber(subjectDetails?.studyDiscontinuedReason),
        });
    }
    if (sectionConfig?.lastVisitNameAndDate) {
        items.push({
            key: 'lastVisitName',
            label: tSubjectProfile('lastVisitNameAndDate'),
            value: formatStringOrNumber(subjectDetails?.lastVisitCompletedName),
        });
        items.push({
            key: 'lastVisitDate',
            label: '',
            value: formatDate(subjectDetails?.lastVisitCompletedDateAll),
        });
    }

    if (!isLoading && !isError && items.length === 0) {
        return null;
    }

    return (
        <Card
            data-testid="subject-profile-details-subject-status-card"
            title={
                <div className={styles.cardTitle}>
                    <span>{sectionConfig?.label ?? 'Status'}</span>
                    {sectionConfig?.status && subjectDetails?.status && (
                        <Badge
                            variant="secondary"
                            tooltip={formatStringOrNumber(subjectDetails.status)}
                            maxWidth="30ch"
                        >
                            {formatStringOrNumber(subjectDetails.status)}
                        </Badge>
                    )}
                </div>
            }
            padding="none"
            collapsible
        >
            {isLoading || isError ? (
                <div className={styles.cardLoadingStateWrapper}>
                    {isError ? (
                        <InfoField type="Error" message={t('common:errorMessage')} />
                    ) : isLoading ? (
                        <Loader />
                    ) : null}
                </div>
            ) : (
                <>
                    <DetailsCardDetailList items={items} />

                    <SubjectProfileDetailsMiniWidgets
                        // Should be defined if isLoading and isError are both false
                        config={sectionConfig!}
                        subjectDetails={subjectDetails!}
                        // This might be null/undefined even if it's loaded
                        study={study}
                    />
                </>
            )}
        </Card>
    );
};

export default SubjectProfileDetailsSubjectStatusCard;
